import { Injectable, inject } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { CmsContentMapperService } from './cms-content-mapper.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TimeService } from './time.service';
import { Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ContentUpdaterService } from './content-updater/content-updater.service';

@Injectable({
  providedIn: 'root',
})
export class BlogService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _sanitizer = inject(DomSanitizer);
  private _time = inject(TimeService);
  private _contentUpdate = inject(ContentUpdaterService);

  public countTotal: number;

  /**
   * Get news list from CMS and prepare for using in frontend
   * @param params
   */
  list(params: object = {}): Observable<any> {
    return this._contentUpdate.registerContent('news/list', params, () =>
      this._api.newsList(params).pipe(
        tap(response => this.countTotal = response.data.countTotal),
        map(response => this._mapArticleList(this._mapper.mapCmsData(response.data.list, {
          id: 'id',
          slug: 'alias',
          type: 'type',
          publishAt: 'publishAt.date',
        }))),
      ));
  }

  /**
   * Get news item from CMS and prepare for using in frontend
   *
   * @param params
   */
  item(params: object = {}): Observable<any> {
    return this._contentUpdate.registerContent('news/item', params, () =>
      this._api.newsItem(params).pipe(
        map(response => {
          if (response.statusCode === 200) {
            return this._mapArticleList(this._mapper.mapCmsData(response.data, {
              id: 'id',
              slug: 'alias',
              type: 'type',
              publishAt: 'publishAt.date',
              unpublishAt: 'unpublishAt.date',
            }));
          } else {
            return [{
              statusCode: response.statusCode,
            }];
          }
        }),
      ));
  }

  /**
   * Prepare article list for using in frontend
   *
   * @param list
   * @private
   */
  private _mapArticleList(list: any[]): any[] {
    return list.map(article => ({
      ...article,
      video: this._prepareVideoSrc(article.Video),
      published: this._getDateLabel(article.publishAt),
    }));
  }

  /**
   * Returns correct video src by provided Vimeo link
   *
   * @param src
   * @private
   */
  private _prepareVideoSrc(src: string): SafeResourceUrl {
    if (!src) {
      return;
    }

    const videoId = src.split('/').pop();

    return this._sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`);
  }

  /**
   * Returns correct date label by provided date string
   *
   * @param date
   * @private
   */
  private _getDateLabel(date: string): { num: number | string, label: string, date: Date } {
    const daysCount = this._time.timeDiff(this._time.toLocalDate(date)).days;
    const label = daysCount > 6
      ? this._time.toLocalDate(date).toString()
      : daysCount === 0
        ? 't.today'
        : daysCount === 1
          ? 't.yesterday'
          : 't.days-ago';

    return {
      num: daysCount > 1 ? daysCount : '',
      label,
      date: this._time.toLocalDate(date),
    };
  }
}
